function createPerformanceElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createCard(title, hint) {
  const card = createPerformanceElement('div', 'cc-card');
  const head = createPerformanceElement('div', 'cc-card-head');
  head.append(createPerformanceElement('h3', '', title), createPerformanceElement('span', 'cc-hint', hint));
  card.append(head);
  return card;
}

function createMetric(label, value, foot = '', tone = '', unavailable = false) {
  const card = createPerformanceElement('div', 'cc-card metric');
  card.append(
    createPerformanceElement('div', 'label', label),
    createPerformanceElement('div', `value${unavailable ? ' cc-na' : ''}`, value),
  );
  if (foot) card.append(createPerformanceElement('div', `foot${tone ? ` ${tone}` : ''}`, foot));
  return card;
}

function createTable(headers, rows, emptyMessage) {
  const table = createPerformanceElement('table', 'cc-table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  headRow.append(...headers.map((header) => createPerformanceElement('th', '', header)));
  head.append(headRow);
  const body = document.createElement('tbody');
  if (rows.length) {
    body.append(...rows);
  } else {
    const row = document.createElement('tr');
    const cell = createPerformanceElement('td', 'muted', emptyMessage);
    cell.colSpan = headers.length;
    row.append(cell);
    body.append(row);
  }
  table.append(head, body);
  const wrapper = createPerformanceElement('div', 'cc-table-scroll');
  wrapper.append(table);
  return wrapper;
}

function createDependencyList(providers, helpers) {
  const list = createPerformanceElement('div', 'cc-dep-list');
  const rows = (providers || []).slice(0, 8);
  if (!rows.length) {
    list.append(createPerformanceElement('div', 'muted', '当前时间范围内暂无依赖调用样本'));
    return list;
  }
  rows.forEach((provider) => {
    const failRate = Number(provider.failRate || 0);
    const meter = document.createElement('progress');
    meter.className = `cc-progress-meter${failRate >= 0.05 ? ' bad' : ''}`;
    meter.max = 100;
    meter.value = Math.min(100, Math.max(6, failRate * 400));
    const name = createPerformanceElement('strong', '', helpers.providerLabel(provider.provider));
    name.title = provider.provider || '';
    const row = createPerformanceElement('div', 'cc-dep-row');
    row.append(
      name,
      createPerformanceElement('span', failRate >= 0.05 ? 'delta-down' : 'muted', helpers.pct(failRate)),
      createPerformanceElement('span', 'mono', helpers.ms(provider.p90Ms ?? provider.avgMs)),
      meter,
    );
    list.append(row);
  });
  return list;
}

function createKpis(items) {
  const kpis = createPerformanceElement('div', 'cc-kpis');
  items.forEach(([label, value, tone = '']) => {
    const item = document.createElement('div');
    item.append(createPerformanceElement('span', 'muted', label), createPerformanceElement('strong', tone, value));
    kpis.append(item);
  });
  return kpis;
}

function createPhaseList(phases, helpers) {
  const list = createPerformanceElement('div', 'cc-phase-list');
  if (!phases.length) {
    list.append(createPerformanceElement('div', 'muted', '暂无流水线样本'));
    return list;
  }
  const maxPhaseMs = Math.max(1, ...phases.map((phase) => Number(phase.p90Ms ?? phase.avgMs ?? phase.maxMs ?? 0)));
  phases.forEach((phase) => {
    const phaseMs = Number(phase.p90Ms ?? phase.avgMs ?? phase.maxMs ?? 0);
    const label = document.createElement('span');
    label.append(
      createPerformanceElement('strong', '', helpers.phaseLabel(phase.phase)),
      createPerformanceElement('span', 'mono', ` · ${phase.jobType || ''}`),
    );
    const meter = document.createElement('progress');
    meter.className = 'cc-progress-meter';
    meter.max = 100;
    meter.value = Math.max(4, Math.min(100, (phaseMs / maxPhaseMs) * 100));
    const row = createPerformanceElement('div', 'cc-phase-row');
    row.append(label, meter, createPerformanceElement('span', 'mono', helpers.duration(phaseMs)));
    list.append(row);
  });
  return list;
}

export function renderCommandCenterPerformance(root, data, helpers) {
  if (!root) return;
  const http = data.http || {};
  const windowMinutes = Number(data.windowMinutes || 60);
  const httpAvailable = Boolean(http.available);
  const rateLimitsAvailable = Boolean(data.rateLimits?.available);
  const averageQps = httpAvailable
    ? (Number(http.total || 0) / Math.max(60, windowMinutes * 60)).toFixed(2)
    : helpers.unavailableLabel(http.reason);
  const pValues = httpAvailable
    ? `${helpers.duration(http.p50Ms)} / ${helpers.duration(http.p90Ms ?? http.avgMs)} / ${helpers.duration(http.p99Ms)}`
    : helpers.unavailableLabel(http.reason);

  const metrics = createPerformanceElement('div', 'cc-grid-3');
  metrics.append(
    createMetric('平均 QPS', averageQps, httpAvailable ? `${Number(http.total || 0)} 次请求` : '', '', !httpAvailable),
    createMetric('HTTP p50 / p90 / p99', pValues, httpAvailable && !http.histogramAvailable ? '分位直方图未接入，p90 使用平均值' : '', '', !httpAvailable),
    createMetric('限流拒绝', rateLimitsAvailable ? String(data.rateLimits.total || 0) : helpers.unavailableLabel(data.rateLimits?.reason), `${windowMinutes} 分钟累计`, Number(data.rateLimits?.total || 0) > 20 ? 'delta-warn' : '', !rateLimitsAvailable),
  );

  const slowRoutes = createCard('响应较慢的请求', '路由已归一化');
  if (data.slowRoutes?.available === false) {
    slowRoutes.append(createPerformanceElement('span', 'cc-na', helpers.unavailableLabel(data.slowRoutes.reason)));
  } else {
    const rows = (data.slowRoutes?.rows || []).slice(0, 6).map((route) => {
      const row = document.createElement('tr');
      row.append(
        createPerformanceElement('td', 'mono', `${route.method || ''} ${route.route || ''}`.trim()),
        createPerformanceElement('td', '', helpers.duration(route.p90Ms ?? route.avgMs)),
        createPerformanceElement('td', '', helpers.pct(route.error5xxRate)),
        createPerformanceElement('td', '', String(route.count ?? '')),
      );
      return row;
    });
    slowRoutes.append(createTable(['请求路径', '90 分位', '服务异常率', '请求数'], rows, '暂无数据'));
  }

  const dependencies = createCard('依赖雷达', '失败率 / p90');
  if (data.dependencies?.available === false) {
    dependencies.append(createPerformanceElement('span', 'cc-na', helpers.unavailableLabel(data.dependencies.reason)));
  } else {
    dependencies.append(createDependencyList(data.dependencies?.providers || [], helpers));
  }
  const performanceGrid = createPerformanceElement('div', 'cc-grid-2');
  performanceGrid.append(slowRoutes, dependencies);

  const phases = createCard('业务流水线阶段', 'Listing / 图片任务耗时');
  if (data.pipelinePhases?.available === false) {
    phases.append(createPerformanceElement('span', 'cc-na', helpers.unavailableLabel(data.pipelinePhases.reason)));
  } else {
    phases.append(createPhaseList(data.pipelinePhases?.phases || [], helpers));
  }
  const runtime = createCard('应用运行时', '实时进程状态');
  if (data.runtime?.available) {
    runtime.append(createKpis([
      ['RSS 内存', `${Number(data.runtime.memRssMb || 0)}MB`],
      ['堆内存', `${Number(data.runtime.memHeapMb || 0)}MB`],
      ['运行时间', `${Math.round(Number(data.runtime.uptimeSec || 0) / 3600)}h`],
      ['运行时采样', data.runtime.series?.available ? '已接入' : '实时值'],
    ]));
  } else {
    runtime.append(createPerformanceElement('span', 'cc-na', helpers.unavailableLabel(data.runtime?.reason)));
  }
  const agent = createCard('Agent', '运行 span 聚合');
  if (data.agent?.available) {
    agent.append(createKpis([
      ['Agent 运行', String(Number(data.agent.total || 0))],
      ['平均耗时', helpers.duration(data.agent.avgMs)],
      ['异常率', helpers.pct(data.agent.errorRate), Number(data.agent.errorRate || 0) > 0.08 ? 'delta-down' : ''],
      ['统计窗口', `${windowMinutes}m`],
    ]));
  } else {
    agent.append(createPerformanceElement('span', 'cc-na', helpers.unavailableLabel(data.agent?.reason)));
  }
  const stack = createPerformanceElement('div', 'cc-stack');
  stack.append(runtime, agent);
  const runtimeGrid = createPerformanceElement('div', 'cc-grid-2');
  runtimeGrid.append(phases, stack);

  root.replaceChildren(metrics, performanceGrid, runtimeGrid);
}
